import React, { useCallback, forwardRef, useEffect } from 'react'

import { ProductV2, PurchaseGetDraftResponse, formatPriceText } from '@common'
import cx from 'classnames'
import { AnimatePresence, m } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/legacy/image'

import { PillCounter } from '@/components/buttons/PillCounter'
import { useLocale } from '@/features/locale/useLocale'
import { shortSlugToGameNameKey } from '@/localization/utils'
import { resolveProductImage } from '@/utils/resolveProductImage'
import { useAppDispatch } from '@/utils/useAppDispatch'
import { useAppSelector } from '@/utils/useAppSelector'

import { Alert } from '../../components/Icons'
import { useProductName } from '../../utils/useProductName'
import { CheckoutRow, addProductThunk, removeProductThunk } from '../checkout/checkoutSlice'
import { useGames } from '../common/useGames'

import styles from './ProductRow.module.scss'

type ProductRowProps = {
  increment: (product: ProductV2) => void
  decrement: (product: ProductV2) => void
  row: CheckoutRow
  draft?: PurchaseGetDraftResponse
  className?: string
  locked?: boolean
}

export const ProductRowList = ({
  products,
  draft,
  className,
  locked,
}: {
  products: CheckoutRow[]
  draft?: PurchaseGetDraftResponse
  className?: string
  locked?: boolean
}) => {
  const dispatch = useAppDispatch()
  const increment = useCallback((product: ProductV2) => dispatch(addProductThunk(product)), [dispatch])
  const decrement = useCallback((product: ProductV2) => dispatch(removeProductThunk(product)), [dispatch])
  const addItemNotification = useAppSelector((state) =>
    state.notifications.notifications.find((a) => a.type === 'ADD_PRODUCT'),
  )

  const refs = products.reduce<Record<string, React.RefObject<HTMLDivElement>>>((acc, row) => {
    acc[row.product.id] = React.createRef()
    return acc
  }, {})

  useEffect(() => {
    if (!addItemNotification) {
      return
    }
    const shouldScroll = addItemNotification.type === 'ADD_PRODUCT'
    const element = refs[addItemNotification.productId]?.current
    if (shouldScroll && element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [addItemNotification, refs])

  return (
    <>
      {products.map((row) => (
        <ProductRow
          key={row.product.id}
          ref={refs[row.product.id]}
          row={row}
          increment={increment}
          decrement={decrement}
          draft={draft}
          className={className}
          locked={locked}
        />
      ))}
    </>
  )
}

const ProductRow = forwardRef<HTMLDivElement, ProductRowProps>(
  ({ row, increment, decrement, draft, className, locked }, ref) => {
    const t = useTranslations()
    const games = useGames()
    const locale = useLocale()
    const quantity = row.quantity
    const market = useAppSelector((state) => state.config.market)
    const shortSlug = games.find((g) => g.slug === row.product.game)?.shortSlug
    const inEligible = draft?.rows?.find((r) => r.product.id === row.product.id)?.eligibility?.isEligible === false
    const showItemAdded = useAppSelector((state) =>
      state.notifications.notifications.some((n) => n.type === 'ADD_PRODUCT' && n.productId === row.product.id),
    )
    const currency = draft?.checkout.currency ?? market?.currencyCode
    const currencyCountry = draft?.checkout.currencyCountry ?? market?.currencyCountry
    const productName = useProductName(row.product)

    if (!shortSlug) {
      throw new Error('invalid game')
    }

    return (
      <AnimatePresence initial={showItemAdded}>
        <div ref={ref} className={cx(styles.Item, { [styles.InEligible]: inEligible }, className)}>
          <m.div initial={{ x: '50%', opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.3 }}>
            <div className={styles.Row}>
              <div className={styles.ItemTitle}>
                <strong>{productName}</strong>
                <p title={t(shortSlugToGameNameKey(shortSlug))}>{t(shortSlugToGameNameKey(shortSlug))}</p>
              </div>
              <div className={styles.ItemImageContainer}>
                <div className={styles.ItemImageBackground}>
                  <div className={cx(styles.MinWidth, styles.ItemImage)}>
                    <Image
                      src={resolveProductImage(row.product)}
                      layout="fixed"
                      width={65}
                      height={65}
                      objectFit="contain"
                      alt=""
                      placeholder="empty"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.Row}>
              <div className={styles.PillCounter}>
                {locked ? (
                  <div className={styles.ItemPrice}>{quantity}</div>
                ) : (
                  <PillCounter
                    error={inEligible}
                    increment={() => increment(row.product)}
                    decrement={() => decrement(row.product)}
                    min={0}
                    max={10}
                    count={quantity}
                    data-cy={`cart-pillcounter-${row.product.id}`}
                  />
                )}
                {inEligible ? (
                  <div className={styles.Alert}>
                    <Alert type="error" />
                  </div>
                ) : null}
              </div>
              <div className={cx(styles.ItemPrice, { [styles.InEligible]: inEligible })}>
                {currency && currencyCountry
                  ? formatPriceText((row.product.prices[currencyCountry] ?? 0) * quantity, locale, currency)
                  : null}
              </div>
            </div>
          </m.div>
        </div>
      </AnimatePresence>
    )
  },
)

ProductRow.displayName = 'ProductRow'
